import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { useSubscription } from '../../context/SubscriptionContext';
import UpgradePromptModal from '../subscription/UpgradePromptModal';
import UsageIndicator from '../subscription/UsageIndicator';
import { useNavigate } from 'react-router-dom';
import '../../styles/InviteOwnerModal.css';

const InviteOwnerModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation(['dogs', 'common']);
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [limitData, setLimitData] = useState(null);
    const [isCheckingLimit, setIsCheckingLimit] = useState(false);

    const navigate = useNavigate();

    const {
        canAddDog,
        usageData,
        refreshUsageData
    } = useSubscription();

    // Check limits when modal opens
    useEffect(() => {
        if (isOpen) {
            checkDogLimit();
        }
    }, [isOpen]);

    const checkDogLimit = async () => {
        setIsCheckingLimit(true);
        try {
            const result = await canAddDog();
            if (!result.canAddDog) {
                setLimitData(result);
            } else {
                setLimitData(null);
            }
        } catch (err) {
            console.error('Error checking dog limit:', err);
        } finally {
            setIsCheckingLimit(false);
        }
    };

    // Reset states when modal closes
    const handleClose = () => {
        setEmail('');
        setError('');
        setSuccess('');
        setLimitData(null);
        setShowUpgradeModal(false);
        onClose();
    };

    const handleUpgradeClick = () => {
        handleClose();
        navigate('/subscription/plans');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);

        // Check limit before submitting
        const limitCheck = await canAddDog();
        if (!limitCheck.canAddDog) {
            setLimitData(limitCheck);
            setShowUpgradeModal(true);
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await axios.post('/api/v1/auth/invite/owner', { email }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            const responseData = response.data;
            if (responseData?.status === 'SCHOOL_INVITATION_SENT') {
                setSuccess(t('dogs:owners.invite.schoolInviteSent'));
            } else {
                setSuccess(t('dogs:owners.invite.registrationInviteSent'));
            }

            // Refresh usage data after successful invite
            refreshUsageData();

            // Show success message and close after delay
            setTimeout(() => {
                handleClose();
            }, 6000);

        } catch (err) {
            // Check if this is an owner limit error (403)
            if (err.response?.status === 403 && err.response?.data?.errors?.upgradeRequired) {
                const errorData = err.response.data.errors;
                setLimitData({
                    currentDogs: parseInt(errorData.currentDogs) || 0,
                    maxDogs: parseInt(errorData.maxDogs) || 0,
                    planName: errorData.planName || t('dogs:owners.invite.unknownPlan'),
                    upgradeRequired: true
                });
                setShowUpgradeModal(true);
            } else if (err.response?.status === 409) {
                setError(t('dogs:owners.invite.emailAlreadyUsed'));
            } else {
                setError(t('dogs:owners.invite.error'));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Don't render anything if modal is not open
    if (!isOpen) return null;

    // Show upgrade modal if limit is reached
    if (showUpgradeModal && limitData) {
        return (
            <UpgradePromptModal
                isOpen={true}
                onClose={() => setShowUpgradeModal(false)}
                currentDogs={limitData.currentDogs}
                maxDogs={limitData.maxDogs}
                planName={limitData.planName}
                suggestedPlanName={limitData.suggestedUpgradePlanName}
                availableUpgrades={limitData.availableUpgrades}
            />
        );
    }

    // Show limit warning if at limit (but modal not shown yet)
    const isAtLimit = limitData && !limitData.canAddDog;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{t('dogs:owners.invite.title')}</h2>
                </div>

                {/* Show usage indicator */}
                {usageData && (
                    <div className="usage-section">
                        <UsageIndicator
                            currentDogs={usageData.currentDogs}
                            maxDogs={usageData.maxDogs}
                            planName={usageData.planName}
                            showUpgradeLink={false}
                            compact={true}
                        />
                    </div>
                )}

                {/* Show limit reached message */}
                {isAtLimit && (
                    <div className="limit-reached-message">
                        <p>{t('dogs:owners.invite.limitReached')}</p>
                        <button
                            type="button"
                            className="upgrade-button"
                            onClick={handleUpgradeClick}
                        >
                            {t('dogs:owners.invite.upgradePlan')}
                        </button>
                    </div>
                )}

                {/* Only show form if not at limit */}
                {!isAtLimit && (
                    <form className="invite-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <input
                                id="email"
                                type="email"
                                placeholder={t('dogs:owners.invite.emailPlaceholder')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>

                        {error && <p className="error-message">{error}</p>}
                        {success && <p className="success-message">{success}</p>}

                        <div className="form-actions">
                            <button
                                type="button"
                                className="cancel-button"
                                onClick={handleClose}
                            >
                                {t('common:cancel')}
                            </button>
                            <button
                                type="submit"
                                className="submit-button"
                                disabled={isSubmitting || isCheckingLimit}
                            >
                                {isSubmitting ? t('dogs:owners.invite.sending') : isCheckingLimit ? t('dogs:owners.invite.checking') : t('dogs:owners.invite.sendButton')}
                            </button>
                        </div>
                    </form>
                )}

                {/* Show close button if at limit */}
                {isAtLimit && (
                    <div className="form-actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={handleClose}
                        >
                            {t('common:close')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InviteOwnerModal;
